'use client';

import { useEffect } from 'react';
import { usePathname, useSearchParams } from 'next/navigation';
import type { EventParams } from '@/types/gtag';

export function useGoogleAnalytics(): void {
  const pathname = usePathname();
  const searchParams = useSearchParams();

  useEffect(() => {
    if (process.env.NODE_ENV !== 'production') return;
    
    const measurementId = process.env.NEXT_PUBLIC_GA_MEASUREMENT_ID;
    if (!measurementId) return;

    if (!window.dataLayer) {
      const script = document.createElement('script');
      script.src = `https://www.googletagmanager.com/gtag/js?id=${measurementId}`;
      script.async = true;
      document.head.appendChild(script);

      window.dataLayer = [];
      window.gtag = function gtag(...args: unknown[]) {
        window.dataLayer.push(args);
      };
      
      window.gtag('js', new Date());
      window.gtag('config', measurementId);
    }

    const url = pathname + (searchParams.toString() ? `?${searchParams}` : '');
    window.gtag('event', 'page_view', { page_path: url });
  }, [pathname, searchParams]);
}

export function trackEvent(eventName: string, eventParams: EventParams = {}): void {
  if (typeof window !== 'undefined' && window.gtag) {
    window.gtag('event', eventName, eventParams);
  }
}

// SEO Performance Tracking
export function trackSEOEvent(action: string, params: EventParams = {}): void {
  trackEvent('seo_interaction', {
    event_category: 'SEO',
    event_label: action,
    ...params
  });
}

// Government Tender Visibility Tracking
export function trackGovernmentTenderInteraction(action: string, page?: string): void {
  trackEvent('government_tender_interaction', {
    event_category: 'Government Tender',
    event_label: action,
    page_location: page || window.location.pathname,
    timestamp: new Date().toISOString()
  });
}

// Varaha Partnership Discoverability Tracking
export function trackVarahaPartnershipInteraction(action: string, context?: string): void {
  trackEvent('varaha_partnership_interaction', {
    event_category: 'Varaha Partnership',
    event_label: action,
    context: context,
    page_location: window.location.pathname,
    timestamp: new Date().toISOString()
  });
}

// Conversion Pathway Tracking
export function trackConversionStep(step: string, value?: number): void {
  trackEvent('conversion_step', {
    event_category: 'Conversion',
    event_label: step,
    value: value,
    page_location: window.location.pathname
  });
}

// Visitor Behavior Flow Tracking
export function trackBehaviorFlow(action: string, fromPage: string, toPage: string): void {
  trackEvent('behavior_flow', {
    event_category: 'Navigation',
    event_label: action,
    from_page: fromPage,
    to_page: toPage,
    timestamp: new Date().toISOString()
  });
}

// Enterprise/Government Solutions Page Tracking
export function trackEnterpriseGovernmentView(section: string): void {
  trackEvent('enterprise_government_view', {
    event_category: 'Enterprise Solutions',
    event_label: section,
    page_location: window.location.pathname
  });
}

// Collaboration Page Tracking
export function trackCollaborationInterest(collaboratorType: string): void {
  trackEvent('collaboration_interest', {
    event_category: 'Collaboration',
    event_label: collaboratorType,
    page_location: window.location.pathname
  });
}

// Pipeline A Conversion Funnel
export function trackPipelineAFunnel(step: 'view' | 'start' | 'submit' | 'success', params: EventParams = {}): void {
  const stepMap = {
    view: 'pipeline_a_form_view',
    start: 'pipeline_a_form_start',
    submit: 'pipeline_a_form_submit',
    success: 'pipeline_a_conversion',
  };
  trackEvent(stepMap[step], {
    event_category: 'Pipeline A - Net Zero Audit',
    funnel_step: step,
    ...params,
  });
}

// Pipeline B Conversion Funnel
export function trackPipelineBFunnel(step: 'view' | 'start' | 'submit' | 'success', params: EventParams = {}): void {
  const stepMap = {
    view: 'pipeline_b_form_view',
    start: 'pipeline_b_form_start',
    submit: 'pipeline_b_form_submit',
    success: 'pipeline_b_conversion',
  };
  trackEvent(stepMap[step], {
    event_category: 'Pipeline B - BRSR Lite Checklist',
    funnel_step: step,
    ...params,
  });
}

// Lead Magnet Funnel
export function trackLeadMagnetFunnel(step: 'view' | 'download' | 'success', resourceName: string, params: EventParams = {}): void {
  trackEvent(`lead_magnet_${step}`, {
    event_category: 'Lead Magnet Library',
    event_label: resourceName,
    funnel_step: step,
    ...params,
  });
}

// GA4 Conversion — generate_lead standard event
export function trackGenerateLead(pipeline: 'A' | 'B', value: number, params: EventParams = {}): void {
  trackEvent('generate_lead', {
    currency: 'INR',
    value,
    lead_source: `Pipeline_${pipeline}`,
    ...params,
  });
}